import React, { useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userPermissions } from '../../../../Constants/User';
import styles from './Navigation.module.less';
import NavigationLink from './NavigationLink/NavigationLink';
import NavigationDropdown from './NavigationLink/NavigationDropdown';
import NavigationCollapse from './NavigationLink/NavigationCollapse';
import { bottomContactsList, contactsList, NavigationPropsTypes } from './Navigation';
import routes from "../../../../references/routes";
import { notificationTagsSelector } from "../../../../store/user/selectors";
import { setShowAiPopup } from "../../../../store/user";
import { fetchNotificationTags } from "../../../../store/user/actions";

/**
 * Part of header. Customer navigation bar
 * @category Header
 * @subcategory Navigation
 * @borrows NavigationPropsTypes as props
 * @component
 * @hideconstructor
 * @example
 * const user = {
 * id: 123,
 * email: 'test@test.com',
 * firstName: 'test',
 * middleName: 'test',
 * lastName: 'test',
 * phone: 'test',
 * phoneCode: 'test',
 * activationState: 'test',
 * hasInnerAgeResult: true,
 * isDnaOnly: true,
 * hasUnusedTest: true,
 * permissions: [],
 * photoSrc: 'test',
 * hasDiyPackage: true,
 * canAddInnerAge: true,
 * }
 * return <CustomerNavigation user={user}/>
 */

export const AskItIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.09351 4.21442C7.89206 5.8396 2.82596 10.5486 2.82593 13.5377C2.83349 17.2738 5.91908 20.2993 9.73901 20.2994C12.7987 20.2994 15.4112 18.3448 16.3093 15.6461L16.8455 16.9048L17.7078 18.9332C16.0241 21.4994 13.0838 23.2017 9.73901 23.2017C4.50982 23.2016 0.275204 19.0585 0.275146 13.9439C0.275146 9.64196 6.97175 3.88188 9.09351 2.16949V4.21442ZM9.73901 11.8091C11.1692 11.8091 12.3201 12.9877 12.3201 14.438C12.3199 15.888 11.1627 17.067 9.73901 17.067C8.31521 17.0668 7.15809 15.8879 7.15796 14.438C7.15796 12.9878 8.31513 11.8093 9.73901 11.8091ZM17.4578 9.47125C18.4889 10.9903 19.2014 12.5439 19.2039 13.9439C19.2039 14.2431 19.1873 14.5387 19.1589 14.8306L18.3933 16.2466L17.0828 13.1627L16.5837 12.941C16.4639 12.2928 16.1708 11.5918 15.7732 10.8746L17.0828 10.3453L17.4578 9.47125ZM10.3767 2.16949C11.5697 3.1323 14.21 5.37472 16.2937 7.91852L15.8093 9.04547L14.8582 9.43024C13.2756 7.18425 11.1065 5.03051 10.3767 4.11285V2.16949Z"
      fill="white"
    />
    <path
      d="M23.2082 11.7909L19.8912 10.2846L18.4009 6.98353L16.9106 10.2846L13.5935 11.7909L16.9106 13.2652L18.4009 16.5983L19.8912 13.2652L23.2082 11.7909Z"
      fill="white"
    />
    <ellipse cx="9.84575" cy="14.4651" rx="2.70414" ry="2.70414" fill="white"/>
  </svg>
);

const CustomerNavigation = (props: NavigationPropsTypes) => {
  const dispatch = useDispatch();
  /** user object from props */
  const { user, mobile, inner } = props;
  const { isDnaOnly, hasAccessToBru } = user;
  const history = useHistory();
  const notificationsTags = useSelector(notificationTagsSelector);
  const hasAccessToSummary = user.permissions.includes(userPermissions.summary);
  const hasAccessToBloodwork = !isDnaOnly && user.permissions.includes(userPermissions.bloodwork);
  const hasAccessToNutrition = user.permissions.includes(userPermissions.nutrition);
  const hasAccessToAge = user.permissions.includes(userPermissions.age);
  const showInnerAgeLink = hasAccessToAge && (user.allowedToSeeInnerAge || user.hasDiyOrderWithInnerAge);
  const askITFirstTime = notificationsTags?.includes('suggest-to-see-ask-section');

  useEffect(() => {
    if (user) {
      dispatch(fetchNotificationTags());
    }
  }, [user, dispatch]);

  return (
    <nav className={`${styles.navbar}  ${mobile ? styles.navbarMobile : ''}`}>
      <div className={styles.navbarList}>
        {hasAccessToSummary
                    && <NavigationLink link="/customer/summary/dashboard" label="Dashboard"/>}
        {hasAccessToBloodwork && !isDnaOnly && !mobile
                    && (
                    <NavigationDropdown link={routes.customer.myResults.main} label="My results">
                      <NavigationLink
                        dropdownLink
                        link={routes.customer.myResults.groups}
                        label="Blood results"
                      />
                      {hasAccessToBru
            && (
            <NavigationLink
              dropdownLink
              link="/customer/diy/upload-blood-result"
              label="Add blood results"
            />
            )}
                      <NavigationLink
                        dropdownLink
                        link="/customer/onboarding/test-list/"
                        label="Orders"
                      />
                    </NavigationDropdown>
                    )}
        {hasAccessToBloodwork && !isDnaOnly && mobile && (
          <NavigationCollapse label="My results">
            <NavigationLink link={routes.customer.myResults.groups} label="Blood results"/>
            {hasAccessToBru
            && <NavigationLink link="/customer/diy" label="Add blood results"/>}
            <NavigationLink link="/customer/onboarding/test-list/" label="Orders"/>
          </NavigationCollapse>
        )}
        {!isDnaOnly && !mobile && (
          <NavigationDropdown link="/customer/nutrition" label="Nutrition">
            {hasAccessToNutrition
            && (
              <NavigationLink
                dropdownLink
                link="/customer/nutrition"
                label="Food Recommendations"
              />
            )}
          </NavigationDropdown>
        )}
        {!isDnaOnly && mobile && (
          <NavigationCollapse label="Nutrition">
            {hasAccessToNutrition
            && (
              <NavigationLink
                dropdownLink
                link="/customer/nutrition"
                label="Food Recommendations"
              />
            )}
          </NavigationCollapse>
        )}
        {showInnerAgeLink && <NavigationLink link="/customer/age" label="InnerAge"/>}
        <NavigationLink link="/customer/genetics-report" label="DNA"/>
        {isDnaOnly
        && <NavigationLink link="/customer/blood-analysis" label="Blood analysis"/>}

        <NavigationLink
          link={routes.customer.askIt}
          label={(
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              className={styles.askIt}
              onClick={(e) => {
                if (askITFirstTime) {
                  e.preventDefault();
                  dispatch(setShowAiPopup(true));
                } else {
                  e.preventDefault();
                  history.push(routes.customer.askIt);
                }
              }}
            >
              <span className={styles.askItText}>Ask Terra</span>
              {askITFirstTime && (
              <span className={styles.dot}/>
              )}
            </div>
          )}
        />
      </div>
      {mobile && inner && (
        <div className={`${styles.navbarList} ${styles.navbarListDivider}`}>
          <NavigationLink link="/customer/settings" label="Profile"/>
          <NavigationLink link="/customer/achievements" label="Achievements"/>
        </div>
      )}
      {!inner && contactsList}
      {!inner && bottomContactsList}
    </nav>
  );
};

export default CustomerNavigation;
